import {
  Agency,
  AgencyUpdatePayload,
  IAgencyClientCopiesAggregations,
  MarketAgencyState,
} from "models/agency"
import { DocType, Document } from "models/documents"

import { AgencyPayload } from "routes/Base/Admin/MarketAgencies/CreateModal"
import { Client } from "models/common"
import { ClientCompanyOption } from "routes/Base/Admin/MarketAgencies/Details/_Clients/AddModal"
import { Company } from "models/company"
import { CopyResource } from "models/copies"
import { Paginated } from "./types"
import { http } from "./http"

export const fetchAgencies = async (_key: string, params?: string) => {
  const searchParams = new URLSearchParams(params)
  const res = await http.get("agencies", { searchParams }).json()
  return res as Paginated<Agency>
}

export const fetchAvailableAgencies = async (_key: string) => {
  const res = await http.get("agencies/available").json()
  return res as Pick<Agency, "id" | "name">[]
}

export const fetchAgency = async (_key: string, id: string) => {
  const res = await http.get(`agencies/${id}`).json()
  return res as Agency
}

export const fetchAgenciesByMarket = async (id: string) => {
  const res = await http.get(`agencies?marketIds=${id}`).json()
  return res as Paginated<Agency>
}

export const createMarketAgency = async (pld: AgencyPayload) => {
  const res = await http.post("agencies/", { json: pld }).json()
  return res as Agency
}

export const updateMarketAgency = async (
  pld: AgencyUpdatePayload,
): Promise<MarketAgencyState> => {
  const { id, ...data } = pld
  return http.patch(`agencies/${pld.id}`, { json: data }).json()
}

export const fetchAgencyClients = async (
  _key: string,
  id?: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http.get(`agencies/${id}/clients`, { searchParams }).json()
  return res as Paginated<Client>
}

type AddMarketAgencyVendorsPayload = {
  id: string
  supplierIds: Array<string>
}
type AddMarketAgencyCompaniesPayload = {
  id: string
  companyIds: Array<string>
}

type AddAgencyClientCompaniesPayload = {
  id: string
  addClientsToAgencyResources: Array<{
    clientId: string
    companyId: string
    clientCompanyAssocCode: string
  }>
}

export const addAgencyClientCompanies = async (
  pld: AddAgencyClientCompaniesPayload,
) => {
  const { id, ...json } = pld
  const res = await http.post(`agencies/${id}/clients`, { json }).json()
  return res
}

type ActionAgencyClientPayload = {
  agencyId: string
  clientId: string
}

type DeleteMarketAgencyVendorPld = {
  agencyId: string
  vendorId: string
}

export const deleteAgencyClient = async (pld: ActionAgencyClientPayload) => {
  await http.delete(`agencies/${pld.agencyId}/clients/${pld.clientId}`)
}

export const fetchAgencyDocuments = async (_key: string, id: string) => {
  const res = await http(
    `agencies/${id}/documents?sort=DATE_CREATED,DESC`,
  ).json()
  return res as Array<Document>
}

type UploadAgencyDocPld = {
  file?: File
  mediaType?: string
  description?: string
  ownerEntityId: string
  documentType?: DocType
}

export const uploadAgencyDocument = async (pld: UploadAgencyDocPld) => {
  if (pld.file === undefined) return
  const { file, ownerEntityId, documentType, description } = pld
  const formData = new FormData()
  formData.append("file", file)
  formData.append("name", file.name)
  if (documentType !== undefined) {
    formData.append("documentType", documentType)
  }
  if (description !== undefined) {
    formData.append("description", description)
  }

  const res = await http
    .post(`agencies/${ownerEntityId}/documents`, {
      body: formData,
      timeout: false,
    })
    .json()
  return res
}

export const deleteAgencyDocument = async (id: string) => {
  await http.delete(`agencies/documents/${id}`)
}

export const deactivateAgency = async (id: string) =>
  await http.patch(`agencies/${id}/deactivate`)

export const deleteAgency = async (id: string) =>
  await http.delete(`agencies/${id}`)

export const fetchMarketAgencyVendors = async (
  _key: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http.get(`suppliers`, { searchParams }).json()
  return res as Paginated<Client>
}

export const addMarketAgencyVendors = async (
  pld: AddMarketAgencyVendorsPayload,
) => {
  const { id, supplierIds } = pld
  return http.post(`agencies/${id}/suppliers`, { json: { supplierIds } })
}

export const deleteMarketAgencyVendor = async (
  pld: DeleteMarketAgencyVendorPld,
) => {
  await http.delete(`agencies/${pld.agencyId}/suppliers/${pld.vendorId}`)
}

export const fetchMarketAgencyClientCompanies = async (
  _key: string,
  agencyId: string,
) => {
  const res = await http.get(`agencies/${agencyId}/available-clients`).json()
  return res as ClientCompanyOption[]
}

export const fetchMarketAgencyCompanies = async (
  _key: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http.get(`companies/`, { searchParams }).json()
  return res as Paginated<Company>
}

export const addMarketAgencyCompanies = async (
  pld: AddMarketAgencyCompaniesPayload,
) => {
  const { id, companyIds } = pld
  return await http.post(`agencies/${id}/company-assoc`, {
    json: { companyIds },
  })
}

type ActivateMarketAgencyPld = {
  id: string
}

export const activateMarketAgency = async (pld: ActivateMarketAgencyPld) => {
  const { id } = pld
  await http.patch(`agencies/${id}/activate`)
}

type UpdateAgencyClientPayload = {
  agencyId?: string
  clientId?: string
  targetClientId?: string
  copyFromClientGroup?: boolean
  clientAgencyConfigId?: string
}

export const updateImportSettings = async (pld: UpdateAgencyClientPayload) => {
  const { agencyId, targetClientId, clientId, copyFromClientGroup } = pld
  const searchParams = new URLSearchParams()
  !!clientId && searchParams.set("clientId", clientId)
  !!copyFromClientGroup &&
    searchParams.set("copyFromClientGroup", copyFromClientGroup.toString())
  return await http.put(
    `agencies/${agencyId}/clients/${targetClientId}/media-config`,
    {
      searchParams,
    },
  )
}

export const updateTeamLeadImportSettings = async (
  pld: UpdateAgencyClientPayload,
) => {
  const { clientAgencyConfigId, clientId, copyFromClientGroup } = pld
  const searchParams = new URLSearchParams()
  !!clientId && searchParams.set("clientId", clientId)
  !!copyFromClientGroup &&
    searchParams.set("copyFromClientGroup", copyFromClientGroup.toString())
  return await http.put(
    `client-agency-configs/${clientAgencyConfigId}/media-config`,
    {
      searchParams,
    },
  )
}

// ** COPIES ** //

export const fetchClientAgencyConfigCopies = async (
  _key: string,
  clientAgencyConfigId?: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http
    .get(`client-agency-configs/${clientAgencyConfigId}/copies`, {
      searchParams,
    })
    .json()
  return res as Paginated<CopyResource>
}

export const activateClientAgencyConfigCopy = async (pld: {
  clientAgencyConfigId: string
  copyId: string
}) => {
  return await http.post(
    `client-agency-configs/${pld.clientAgencyConfigId}/copies/${pld.copyId}/activate`,
  )
}

export const deactivateClientAgencyConfigCopy = async (pld: {
  clientAgencyConfigId: string
  copyId: string
}) => {
  return await http.post(
    `client-agency-configs/${pld.clientAgencyConfigId}/copies/${pld.copyId}/deactivate`,
  )
}

export const deleteClientAgencyConfigCopy = async (pld: {
  clientAgencyConfigId: string
  copyId: string
}) => {
  await http.delete(
    `client-agency-configs/${pld.clientAgencyConfigId}/copies/${pld.copyId}`,
  )
}

export const updateClientAgencyConfigCopyCode = async (pld: {
  clientAgencyConfigId: string
  copyId: string
  externalCode: string
}) => {
  const { clientAgencyConfigId, copyId, externalCode } = pld
  return await http
    .patch(`client-agency-configs/${clientAgencyConfigId}/copies/${copyId}`, {
      json: { externalCode },
    })
    .json()
}

export const fetchClientAgencyAggregations = async (
  _key: string,
  clientAgencyConfigId: string,
) => {
  const res = await http
    .get(`client-agency-configs/${clientAgencyConfigId}/copies/filter-options`)
    .json()
  return res as IAgencyClientCopiesAggregations
}
